import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useStore } from '../context/StoreContext';
import { BillingConfig, Company, DeliverySettings, POSMode, StaffAccessProfile, StaffPermission, User as StaffUser, UserRole } from '../types';
import { Button } from '../components/Button';
import ProtectedAreaGate from '../components/ProtectedAreaGate';
import { User, Building2, Lock, Save, CheckCircle, ShieldCheck, Trash2, Plus, Clock, CalendarDays, ChevronLeft, ChevronRight, Package, Users, ChefHat, SplitSquareHorizontal, Scale, Store, UtensilsCrossed, Receipt, Bike, DatabaseZap, MessageCircle, QrCode, Camera, Info, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { SpotlightTour, SpotlightStep } from '../components/SpotlightTour';
import { advanceFirstAccessTour, getActiveTourArea, stopFirstAccessTour } from '../utils/firstAccessTour';

const staffPermissionOptions: Array<{ key: StaffPermission; label: string; description: string }> = [
  { key: 'POS', label: 'PDV e balcão', description: 'Lançar pedidos e finalizar vendas.' },
  { key: 'PRODUCTS', label: 'Produtos', description: 'Consultar e organizar o cardápio.' },
  { key: 'STOCK', label: 'Estoque', description: 'Consultar e alterar quantidades.' },
  { key: 'REPORTS', label: 'Relatórios', description: 'Ver vendas, caixa e financeiro.' },
  { key: 'SETTINGS', label: 'Configurações', description: 'Acessar configurações protegidas.' },
  { key: 'KITCHEN', label: 'Cozinha', description: 'Acompanhar pedidos em preparo.' },
  { key: 'DELIVERY', label: 'Delivery', description: 'Acompanhar pedidos e entregas.' },
];

const staffPermissionPresets: Record<StaffAccessProfile, StaffPermission[]> = {
  EMPLOYEE: ['POS', 'PRODUCTS', 'KITCHEN', 'DELIVERY'],
  MANAGER: ['POS', 'PRODUCTS', 'STOCK', 'REPORTS', 'SETTINGS', 'KITCHEN', 'DELIVERY'],
  COUNTER: ['POS'],
};

const DELIVERY_FIELD_CLASS = 'mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none focus:border-brand-400';
const DELIVERY_LABEL_CLASS = 'block text-[10px] font-black uppercase tracking-widest text-slate-500';

const staffProfileOptions: Array<{ key: StaffAccessProfile; label: string; description: string }> = [
  { key: 'EMPLOYEE', label: 'Funcionário', description: 'Operação diária sem áreas administrativas.' },
  { key: 'MANAGER', label: 'Gerente', description: 'Acompanha a operação e os indicadores.' },
  { key: 'COUNTER', label: 'Balcão', description: 'Foco em venda rápida no PDV.' },
];

const auditDateKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const isCompanyAuditLog = (log: any) => {
  const action = String(log?.action || '');
  const actor = String(log?.managerName || '');
  return !/mercado pago|área administrativa/i.test(action) && !/consulta de assinatura|flowtab admin/i.test(actor);
};
const recentCompanyAuditLogs = (logs: any[]) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  return (Array.isArray(logs) ? logs : []).filter(log => isCompanyAuditLog(log) && new Date(log.timestamp).getTime() >= cutoff.getTime());
};

type SettingsSectionKey = 'OPERATION' | 'TEAM' | 'SECURITY' | 'ACTIVITY' | 'ACCOUNT';
type SettingsTourStep = SpotlightStep & { section: SettingsSectionKey; ownerOnly?: boolean; standardOnly?: boolean; companyOnly?: boolean; staffCompanyOnly?: boolean; nonAdminOnly?: boolean };

export const settingsTourSteps: SettingsTourStep[] = [
  { section: 'OPERATION', ownerOnly: true, target: 'settings-header', title: 'Central de controle', description: 'As configurações organizam a operação, os acessos, a atividade e os dados da empresa. Este guia não salva nenhuma mudança.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-nav', title: 'Navegue pelas seções', description: 'Operação reúne os recursos do PDV; Equipe controla pessoas; Segurança explica o acesso; Atividade mostra registros; Conta guarda seus dados.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-pos-modes', title: 'Escolha o modo de operação', description: 'Bar e restaurante usa mesas e comandas; Distribuidora prioriza venda direta; Mercado oferece frente de caixa. Alterar o modo muda a interface do PDV.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-presets', title: 'Perfis prontos', description: 'Bar, Restaurante e Lanchonete aplicam conjuntos de recursos de uma vez. Escolha apenas quando quiser realmente mudar a configuração da empresa.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-counter', title: 'Venda balcão', description: 'Atendimento rápido sem abrir uma mesa. Útil para pedidos simples no caixa.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-waiter', title: 'App garçom', description: 'Permite lançar comandas pelo celular. Fica indisponível nos modos Mercado e Distribuidora.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-kitchen', title: 'Tela da cozinha', description: 'Organiza pedidos em etapas como novo, preparando e pronto.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-split', title: 'Dividir conta', description: 'Prepara o atendimento para divisão de itens ou pessoas na hora de cobrar.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-service', title: 'Taxa de serviço', description: 'Ativa a taxa de serviço nas contas de salão quando sua operação utiliza esse adicional.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-stock', title: 'Gestão de estoque', description: 'Controla quantidades e alertas de estoque baixo para os produtos cadastrados.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-shift', title: 'Fechamento de caixa', description: 'Habilita abertura, suprimentos, sangrias e fechamento. Os detalhes aparecem em Relatórios.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-scale', title: 'Balança e venda por peso', description: 'Use apenas se vender produtos por quilo, como buffet ou açougue.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-delivery', title: 'Delivery online', description: 'Ativa o link público de pedidos e o painel de entregas, quando disponível no plano.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-whatsapp', title: 'Automação WhatsApp', description: 'Recurso de avisos automáticos, dependente da API oficial e da disponibilidade no plano.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-invoice-photo', title: 'Foto da nota com IA', description: 'Lê uma foto ou PDF de nota e prepara itens para conferência antes da entrada no estoque. Pode depender do plano.' },
  { section: 'OPERATION', ownerOnly: true, target: 'settings-feature-use-sub-login', title: 'Sublogin por funcionário', description: 'Quando ativo, o sistema pede que dono ou funcionário escolha seu acesso. Desligado, mantém o acesso direto após o login principal.' },
  { section: 'OPERATION', ownerOnly: true, standardOnly: true, target: 'settings-waiter', title: 'Acesso de garçom', description: 'Defina um código da empresa para as comandas. O acesso temporário dura 15 minutos; gerar ou salvar só acontece quando você aciona os botões fora do guia.' },
  { section: 'TEAM', ownerOnly: true, staffCompanyOnly: true, target: 'settings-staff-list', title: 'Equipe cadastrada', description: 'Veja quem já tem acesso, cargo e áreas liberadas. Edite ou remova um perfil real somente quando necessário.' },
  { section: 'TEAM', ownerOnly: true, staffCompanyOnly: true, target: 'settings-staff-form', title: 'Criar acesso de funcionário', description: 'Informe nome, e-mail individual e senha. Funcionários usam esse acesso no login e não criam conta por conta própria.' },
  { section: 'TEAM', ownerOnly: true, staffCompanyOnly: true, target: 'settings-staff-profile', title: 'Perfil de acesso', description: 'Funcionário, Gerente e Balcão trazem permissões iniciais diferentes. Você ainda pode ajustar cada permissão antes de criar.' },
  { section: 'TEAM', ownerOnly: true, staffCompanyOnly: true, target: 'settings-staff-permissions', title: 'Permissões por área', description: 'Marque apenas PDV, Produtos, Estoque, Relatórios, Configurações, Cozinha ou Delivery que a pessoa deve usar.' },
  { section: 'TEAM', ownerOnly: true, staffCompanyOnly: true, target: 'settings-staff-save', title: 'Confirme o novo acesso', description: 'Criar acesso grava um funcionário de verdade. Neste guia, nenhum perfil é criado ou alterado.' },
  { section: 'TEAM', ownerOnly: true, target: 'settings-manager', title: 'Autorizações da gerência', description: 'Estas senhas extras autorizam ações críticas e áreas protegidas. Não são outra conta de funcionário; o limite é de dois gerentes.' },
  { section: 'SECURITY', nonAdminOnly: true, target: 'settings-security', title: 'Acesso seguro', description: 'Abra o tutorial de segurança para revisar perfis, autorização da gerência e cuidados para não compartilhar a senha do dono.' },
  { section: 'ACTIVITY', ownerOnly: true, companyOnly: true, target: 'settings-cleanup', title: 'Economia do banco', description: 'A limpeza remove registros antigos e mesas vazias travadas, preservando histórico financeiro. O guia não executa limpeza.' },
  { section: 'ACTIVITY', nonAdminOnly: true, target: 'settings-calendar', title: 'Calendário de atividades', description: 'Navegue pelos meses e selecione uma data para filtrar ações recentes do estabelecimento.' },
  { section: 'ACTIVITY', nonAdminOnly: true, target: 'settings-audit-list', title: 'Registro das ações', description: 'Veja o que foi feito, por quem e quando. Em uma conta nova, a lista pode estar vazia.' },
  { section: 'ACCOUNT', target: 'settings-profile', title: 'Seu perfil', description: 'Atualize o nome exibido no sistema. O e-mail de login aparece aqui, mas não pode ser alterado nesta tela.' },
  { section: 'ACCOUNT', target: 'settings-password', title: 'Senha de acesso', description: 'Para trocar a senha, confirme a atual e informe uma nova com pelo menos oito caracteres. O guia não abre nem envia senhas.' },
  { section: 'ACCOUNT', ownerOnly: true, companyOnly: true, target: 'settings-company', title: 'Dados do estabelecimento', description: 'Nome, documento e telefone identificam a empresa no FlowTab. Confira antes de atualizar; o guia não modifica esses campos.' }
];

export const Settings = () => {
  const { 
    user, currentCompany, updateProfile, updateCompanySettings, updateFeatureSettings, verifyManagerAuth, 
    addManager, removeManager, getManagers, auditLogs 
  } = useStore();
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authPassword, setAuthPassword] = useState('');
  const [userName, setUserName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyDoc, setCompanyDoc] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [newManagerName, setNewManagerName] = useState('');
  const [newManagerPass, setNewManagerPass] = useState('');
  const [waiterAccessCode, setWaiterAccessCode] = useState('');
  const [temporaryWaiterAccess, setTemporaryWaiterAccess] = useState<{ token: string; expiresInMinutes: number; qrPayload: string } | null>(null);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [isCleaningDatabase, setIsCleaningDatabase] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<any | null>(null);
  const [auditDate, setAuditDate] = useState('');
  const [auditCalendarMonth, setAuditCalendarMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [auditCalendarLogs, setAuditCalendarLogs] = useState(auditLogs);
  const [visibleAuditLogs, setVisibleAuditLogs] = useState(auditLogs);
  const [activeSettingsSection, setActiveSettingsSection] = useState<SettingsSectionKey>('OPERATION');
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const tourOriginalSectionRef = useRef<SettingsSectionKey | null>(null);
  const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null);
  const [staffMembers, setStaffMembers] = useState<StaffUser[]>([]);
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
  const [staffForm, setStaffForm] = useState<{
    name: string;
    email: string;
    password: string;
    accessProfile: StaffAccessProfile;
    accessPermissions: StaffPermission[];
  }>({
    name: '',
    email: '',
    password: '',
    accessProfile: 'EMPLOYEE',
    accessPermissions: staffPermissionPresets.EMPLOYEE,
  });
  const [deliverySettingsDraft, setDeliverySettingsDraft] = useState<DeliverySettings>({ isOpen: true, allowPickup: true, openingTime: '', closingTime: '', minimumOrder: 0, estimatedMinutes: 45, defaultDeliveryFee: 0, zones: [] });

  const managers = getManagers();
  const selectedPosMode = currentCompany?.posMode || 'STANDARD';
  const isTradePosMode = selectedPosMode !== 'STANDARD';
  const currentPlanConfig = useMemo(
    () => billingConfig?.plans.find(plan => plan.plan === currentCompany?.plan) || null,
    [billingConfig?.plans, currentCompany?.plan]
  );
  const planFeatures = currentPlanConfig?.features || {};
  const planName = currentPlanConfig?.name || `FlowTab ${currentCompany?.plan || 'PRO'}`;
  const isFeatureAllowed = (featureKey: string) => planFeatures[featureKey] === true;
  const staffCompanyId = currentCompany?.id || user?.companyId;

  useEffect(() => {
    const settings = currentCompany?.deliverySettings;
    setDeliverySettingsDraft({
      isOpen: settings?.isOpen !== false,
      allowPickup: settings?.allowPickup !== false,
      openingTime: settings?.openingTime || '', closingTime: settings?.closingTime || '',
      minimumOrder: Number(settings?.minimumOrder || 0), estimatedMinutes: Number(settings?.estimatedMinutes || 45),
      defaultDeliveryFee: Number(settings?.defaultDeliveryFee || 0), zones: settings?.zones || []
    });
  }, [currentCompany?.id, currentCompany?.deliverySettings]);
  const settingsSections = useMemo<Array<{ key: SettingsSectionKey; label: string; description: string; icon: typeof Store }>>(() => [
    ...(user.role === UserRole.OWNER ? [{ key: 'OPERATION' as const, label: 'Operação', description: 'PDV e recursos', icon: Store }] : []),
    ...(user.role === UserRole.OWNER ? [{ key: 'TEAM' as const, label: 'Equipe', description: 'Acessos e cargos', icon: Users }] : []),
    ...(user.role !== UserRole.SAAS_ADMIN ? [{ key: 'SECURITY' as const, label: 'Segurança', description: 'Tutorial e proteção', icon: ShieldCheck }] : []),
    ...(user.role !== UserRole.SAAS_ADMIN ? [{ key: 'ACTIVITY' as const, label: 'Atividade', description: 'Logs e manutenção', icon: Clock }] : []),
    { key: 'ACCOUNT' as const, label: 'Conta', description: 'Perfil e empresa', icon: User },
  ], [user.role]);
  const tourStorageKey = `flowtab-settings-tour-v1:${user?.id}`;
  const availableTourSteps = useMemo(() => settingsTourSteps.filter(step =>
    (!step.ownerOnly || user?.role === UserRole.OWNER) &&
    (!step.nonAdminOnly || user?.role !== UserRole.SAAS_ADMIN) &&
    (!step.standardOnly || !isTradePosMode) &&
    (!step.companyOnly || Boolean(currentCompany)) &&
    (!step.staffCompanyOnly || Boolean(staffCompanyId))
  ), [user?.role, isTradePosMode, currentCompany, staffCompanyId]);

  const startTour = useCallback(() => {
    if (!availableTourSteps.length) return;
    tourOriginalSectionRef.current = activeSettingsSection;
    setActiveSettingsSection(availableTourSteps[0].section);
    setTourStep(0);
    setTourOpen(true);
  }, [activeSettingsSection, availableTourSteps]);

  const changeTourStep = (index: number) => {
    setActiveSettingsSection(availableTourSteps[index].section);
    setTourStep(index);
  };

  const finishTour = () => {
    window.localStorage.setItem(tourStorageKey, 'seen');
    void api.auth.completeTutorial('settings');
    const nextRoute = user ? advanceFirstAccessTour(user, 'settings') : null;
    setActiveSettingsSection(tourOriginalSectionRef.current || 'ACCOUNT');
    tourOriginalSectionRef.current = null;
    setTourOpen(false);
    setTourStep(0);
    if (nextRoute) navigate(nextRoute, { replace: true });
  };

  useEffect(() => {
    if (!isAuthenticated || !user?.id || tourOpen) return;
    if (getActiveTourArea(user.id) === 'settings') {
      startTour();
      return;
    }
    let active = true;
    const checkTour = async () => {
      const status = await api.auth.getTutorialStatus('settings');
      if (!active) return;
      const seenLocally = window.localStorage.getItem(tourStorageKey) === 'seen';
      if (seenLocally && status && !status.completed) void api.auth.completeTutorial('settings');
      if (seenLocally || status?.completed) {
        window.localStorage.setItem(tourStorageKey, 'seen');
        return;
      }
      startTour();
    };
    void checkTour();
    return () => { active = false; };
  }, [isAuthenticated, user?.id, tourOpen, tourStorageKey, startTour]);

  useEffect(() => {
    setVisibleAuditLogs(recentCompanyAuditLogs(auditLogs));
    setAuditCalendarLogs(recentCompanyAuditLogs(auditLogs));
  }, [auditLogs]);

  useEffect(() => {
    if (!isAuthenticated || !currentCompany?.id) return;
    let active = true;
    api.auditLogs.getByCompany(currentCompany.id, auditDate)
      .then(logs => {
        if (active) setVisibleAuditLogs(recentCompanyAuditLogs(logs || []));
      })
      .catch(() => {
        if (active) setVisibleAuditLogs([]);
      });
    return () => {
      active = false;
    };
  }, [auditDate, currentCompany?.id, isAuthenticated]);

  const auditCalendarDays = useMemo(() => {
    const monthStart = new Date(auditCalendarMonth.getFullYear(), auditCalendarMonth.getMonth(), 1);
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(monthStart.getDate() - monthStart.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(calendarStart);
      date.setDate(calendarStart.getDate() + index);
      const key = auditDateKey(date);
      return {
        date,
        key,
        isCurrentMonth: date.getFullYear() === auditCalendarMonth.getFullYear() && date.getMonth() === auditCalendarMonth.getMonth(),
        isToday: key === auditDateKey(new Date()),
        logCount: auditCalendarLogs.filter(log => auditDateKey(new Date(log.timestamp)) === key).length,
      };
    });
  }, [auditCalendarLogs, auditCalendarMonth]);

  const changeAuditCalendarMonth = (direction: -1 | 1) => {
    setAuditCalendarMonth(month => new Date(month.getFullYear(), month.getMonth() + direction, 1));
  };

  useEffect(() => {
    if (!isAuthenticated || !currentCompany?.id) return;
    const monthKey = `${auditCalendarMonth.getFullYear()}-${String(auditCalendarMonth.getMonth() + 1).padStart(2, '0')}`;
    let active = true;
    api.auditLogs.getByCompany(currentCompany.id, undefined, monthKey)
      .then(logs => {
        if (active) setAuditCalendarLogs(recentCompanyAuditLogs(logs || []));
      })
      .catch(() => {
        if (active) setAuditCalendarLogs([]);
      });
    return () => {
      active = false;
    };
  }, [auditCalendarMonth, currentCompany?.id, isAuthenticated]);

  useEffect(() => {
    if (user) {
        setUserName(user.name);
        if (user.role === UserRole.SAAS_ADMIN) setIsAuthenticated(true);
    }
    if (currentCompany) {
        setCompanyName(currentCompany.name);
        setCompanyDoc(currentCompany.document);
        setCompanyPhone(currentCompany.contactPhone || '');
        setWaiterAccessCode(currentCompany.waiterAccessCode || '');
    }
  }, [user, currentCompany]);

  useEffect(() => {
    if (!settingsSections.some(section => section.key === activeSettingsSection)) {
      setActiveSettingsSection(settingsSections[0]?.key || 'ACCOUNT');
    }
  }, [activeSettingsSection, settingsSections, user?.role]);

  useEffect(() => {
    let mounted = true;
    api.billing.getConfig()
      .then(config => {
        if (mounted) setBillingConfig(config);
      })
      .catch(() => null);
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== UserRole.OWNER || !staffCompanyId) return;
    let mounted = true;
    api.users.getUsersByCompany(staffCompanyId)
      .then(users => {
        if (mounted) setStaffMembers((users || []).filter((profile: StaffUser) => profile.role === UserRole.CASHIER));
      })
      .catch(() => {
        if (mounted) setStaffMembers([]);
      });
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, staffCompanyId, user?.role]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await verifyManagerAuth(authPassword)) {
        setIsAuthenticated(true);
    } else {
        alert("Senha incorreta. Acesso negado.");
    }
  };

  const handleUserUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isChangingPassword && userPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'A confirmação da nova senha não confere.' });
      return;
    }
    const success = await updateProfile(userName, isChangingPassword ? userPassword : undefined, isChangingPassword ? currentPassword : undefined);
    setMessage(success
      ? { type: 'success', text: isChangingPassword ? 'Senha alterada com segurança!' : 'Perfil atualizado!' }
      : { type: 'error', text: 'Não foi possível salvar. Confira a senha atual e tente novamente.' });
    if (success) {
      setCurrentPassword('');
      setUserPassword('');
      setConfirmPassword('');
      setIsChangingPassword(false);
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleCompanyUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await updateCompanySettings(companyName, companyDoc, companyPhone);
    setMessage(success ? { type: 'success', text: 'Dados da empresa atualizados!' } : { type: 'error', text: 'Erro ao atualizar.' });
    setTimeout(() => setMessage(null), 3000);
  };
  
  const handleWaiterCodeUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isTradePosMode) {
      setMessage({ type: 'error', text: 'Garçom fica desativado nos modos Mercado e Distribuidora.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    const success = await updateFeatureSettings({ waiterAccessCode });
    setMessage(success ? { type: 'success', text: 'Código de acesso salvo!' } : { type: 'error', text: 'Erro ao salvar.' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleTemporaryWaiterAccess = async () => {
    if (!currentCompany?.id) return;
    if (isTradePosMode) {
      setMessage({ type: 'error', text: 'Garçom fica desativado nos modos Mercado e Distribuidora.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    const result = await api.auth.createWaiterAccessToken(currentCompany.id);
    setTemporaryWaiterAccess(result);
    if (result?.token && navigator.clipboard) {
      navigator.clipboard.writeText(result.token).catch(() => null);
    }
    setMessage(result ? { type: 'success', text: 'Acesso temporário gerado por 15 minutos.' } : { type: 'error', text: 'Não foi possível gerar o acesso temporário.' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleFeatureToggle = async (settings: Partial<Company>) => {
    const blockedFeature = Object.keys(settings).find(key => (
      settings[key as keyof Company] === true &&
      Object.prototype.hasOwnProperty.call(planFeatures, key) &&
      !isFeatureAllowed(key)
    ));
    if (blockedFeature) {
      const featureName = billingConfig?.featureCatalog?.[blockedFeature] || 'este recurso';
      setMessage({ type: 'error', text: `${featureName} não está disponível no ${planName}.` });
      setTimeout(() => setMessage(null), 3500);
      return;
    }
    const success = await updateFeatureSettings(settings);
    setMessage(success ? { type: 'success', text: 'Configuração salva!' } : { type: 'error', text: 'Erro ao salvar.' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDeliveryRulesSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const success = await updateFeatureSettings({ deliverySettings: deliverySettingsDraft });
    setMessage(success ? { type: 'success', text: 'Regras do delivery salvas!' } : { type: 'error', text: 'Não foi possível salvar as regras do delivery.' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDatabaseCleanup = async () => {
    if (!currentCompany || isCleaningDatabase) return;
    setIsCleaningDatabase(true);
    const result = await api.maintenance.runCompanyCleanup(currentCompany.id);
    setCleanupResult(result);
    setMessage(result?.success ? { type: 'success', text: 'Limpeza segura concluída!' } : { type: 'error', text: 'Não foi possível limpar agora.' });
    setIsCleaningDatabase(false);
    setTimeout(() => setMessage(null), 4000);
  };

  const handleOperationPreset = async (preset: 'bar' | 'restaurant' | 'snack') => {
    const presets: Record<typeof preset, Partial<Company>> = {
      bar: { posMode: 'STANDARD', useCounterMode: true, useWaiterApp: false, useKitchenDisplay: false, useSplitBill: false, useServiceFee: true, useStockManagement: true, useShiftManagement: true, useScale: false, useDelivery: false, useWhatsappAutomation: false },
      restaurant: { posMode: 'STANDARD', useCounterMode: false, useWaiterApp: true, useKitchenDisplay: true, useSplitBill: true, useServiceFee: true, useStockManagement: true, useShiftManagement: true, useScale: false, useDelivery: true, useWhatsappAutomation: false },
      snack: { posMode: 'STANDARD', useCounterMode: true, useWaiterApp: false, useKitchenDisplay: true, useSplitBill: false, useServiceFee: true, useStockManagement: true, useShiftManagement: true, useScale: false, useDelivery: true, useWhatsappAutomation: false },
    };

    await handleFeatureToggle(presets[preset]);
  };

  const handlePosModeChange = async (mode: POSMode) => {
    const presets: Record<POSMode, Partial<Company>> = {
      STANDARD: {
        posMode: 'STANDARD',
        useCounterMode: currentCompany?.useCounterMode ?? false,
        useWaiterApp: currentCompany?.useWaiterApp ?? false,
        useKitchenDisplay: currentCompany?.useKitchenDisplay ?? false,
        useSplitBill: currentCompany?.useSplitBill ?? false,
        useServiceFee: currentCompany?.useServiceFee ?? true,
        useStockManagement: currentCompany?.useStockManagement ?? false,
        useShiftManagement: currentCompany?.useShiftManagement ?? false,
        useScale: currentCompany?.useScale ?? false,
        useDelivery: currentCompany?.useDelivery ?? false,
        useWhatsappAutomation: currentCompany?.useWhatsappAutomation ?? false
      },
      DISTRIBUTOR: {
        posMode: 'DISTRIBUTOR',
        useCounterMode: true,
        useWaiterApp: false,
        useKitchenDisplay: false,
        useSplitBill: false,
        useServiceFee: true,
        useStockManagement: true,
        useShiftManagement: true,
        useScale: false,
        useDelivery: true,
        useWhatsappAutomation: false
      },
      MARKET: {
        posMode: 'MARKET',
        useCounterMode: true,
        useWaiterApp: false,
        useKitchenDisplay: false,
        useSplitBill: false,
        useServiceFee: true,
        useStockManagement: true,
        useShiftManagement: true,
        useScale: true,
        useDelivery: false,
        useWhatsappAutomation: false
      }
    };

    await handleFeatureToggle(presets[mode]);
  };

  const handleAddManager = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newManagerName && newManagerPass) {
        const result = await addManager(newManagerName, newManagerPass);
        setMessage({ type: result.success ? 'success' : 'error', text: result.message });
        if (result.success) {
          setNewManagerName('');
          setNewManagerPass('');
        }
        setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleRemoveManager = async (id: string) => {
      if (window.confirm("Remover o acesso deste gerente?")) {
          await removeManager(id);
      }
  };

  const handleStaffProfileChange = (accessProfile: StaffAccessProfile) => {
      setStaffForm(current => ({
          ...current,
          accessProfile,
          accessPermissions: staffPermissionPresets[accessProfile],
      }));
  };

  const toggleStaffPermission = (permission: StaffPermission) => {
      setStaffForm(current => ({
          ...current,
          accessPermissions: current.accessPermissions.includes(permission)
            ? current.accessPermissions.filter(item => item !== permission)
            : [...current.accessPermissions, permission],
      }));
  };

  const resetStaffForm = () => {
      setEditingStaffId(null);
      setStaffForm({
          name: '',
          email: '',
          password: '',
          accessProfile: 'EMPLOYEE',
          accessPermissions: staffPermissionPresets.EMPLOYEE,
      });
  };

  const handleEditStaff = (staff: StaffUser) => {
      const accessProfile = staff.accessProfile || 'EMPLOYEE';
      setEditingStaffId(staff.id);
      setStaffForm({
          name: staff.name,
          email: staff.email,
          password: '',
          accessProfile,
          accessPermissions: staff.accessPermissions || staffPermissionPresets[accessProfile],
      });
  };

  const handleSaveStaff = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!staffCompanyId || staffForm.accessPermissions.length === 0) {
          setMessage({ type: 'error', text: 'Escolha pelo menos uma permissão para o funcionário.' });
          setTimeout(() => setMessage(null), 3500);
          return;
      }
      if (!editingStaffId && !staffForm.password) {
          setMessage({ type: 'error', text: 'Crie uma senha para o novo funcionário.' });
          setTimeout(() => setMessage(null), 3500);
          return;
      }

      const payload: Partial<StaffUser> & Pick<StaffUser, 'name' | 'email'> = {
          ...(editingStaffId ? { id: editingStaffId } : {}),
          name: staffForm.name.trim(),
          email: staffForm.email.trim().toLowerCase(),
          role: UserRole.CASHIER,
          companyId: staffCompanyId,
          accessProfile: staffForm.accessProfile,
          accessPermissions: staffForm.accessPermissions,
          ...(staffForm.password ? { password: staffForm.password } : {}),
      };
      const saved = await api.users.save(payload);
      if (!saved) {
          setMessage({ type: 'error', text: 'Não foi possível salvar o acesso. Confira os dados e tente novamente.' });
          setTimeout(() => setMessage(null), 3500);
          return;
      }
      setStaffMembers(current => editingStaffId
        ? current.map(item => item.id === saved.id ? saved : item)
        : [saved, ...current]);
      setMessage({ type: 'success', text: editingStaffId ? 'Acesso do funcionário atualizado.' : 'Acesso criado. O funcionário já pode entrar pelo login inicial.' });
      resetStaffForm();
      setTimeout(() => setMessage(null), 3500);
  };

  const handleDeleteStaff = async (staff: StaffUser) => {
      if (!window.confirm(`Excluir o acesso de ${staff.name}? Essa pessoa não poderá mais entrar.`)) return;
      const deleted = await api.users.delete(staff.id);
      if (deleted === null) {
          setMessage({ type: 'error', text: 'Não foi possível excluir este acesso.' });
          setTimeout(() => setMessage(null), 3500);
          return;
      }
      setStaffMembers(current => current.filter(item => item.id !== staff.id));
      if (editingStaffId === staff.id) resetStaffForm();
      setMessage({ type: 'success', text: 'Acesso do funcionário removido.' });
      setTimeout(() => setMessage(null), 3500);
  };

  const handleOpenAccessTutorial = () => {
      window.dispatchEvent(new Event('flowtab:open-access-tutorial'));
  };

  if (!user) return null;

  if (!isAuthenticated) {
      return <ProtectedAreaGate title="Configurações protegidas" description={user && getActiveTourArea(user.id) === 'settings' ? 'Confirme a senha autorizada para continuar o tour completo. O guia de Configurações abrirá automaticamente.' : 'Insira sua senha de dono ou de um gerente autorizado para administrar acessos e preferências da empresa.'} password={authPassword} onPasswordChange={setAuthPassword} onSubmit={handleAuth} onBack={() => { if (user && getActiveTourArea(user.id) === 'settings') { stopFirstAccessTour(user.id); navigate('/pos'); } else navigate(-1); }} submitLabel="Acessar configurações" accent="brand" />;
  }

  return (
    <div className="flex flex-col flex-1 bg-gray-50">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-5xl pb-20">
            <div data-tour="settings-header" className="mb-6 rounded-[2rem] bg-slate-950 p-6 text-white shadow-xl shadow-slate-200 sm:p-8">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-cyan-200">Central de controle</p>
                        <h1 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">Configurações</h1>
                        <p className="mt-2 max-w-xl text-sm leading-6 text-slate-300">Organize a operação, os acessos e os dados da sua empresa em um só lugar.</p>
                        <button type="button" onClick={startTour} aria-label="Abrir guia das Configurações" className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-emerald-200 hover:bg-white/20"><HelpCircle className="h-4 w-4" /> Guia das Configurações</button>
                    </div>
                    <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-bold text-slate-300"><ShieldCheck className="h-5 w-5 text-emerald-300" /> Área protegida</div>
                </div>
            </div>

            {message && <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}><CheckCircle className="w-5 h-5" />{message.text}</div>}

            <nav data-tour="settings-nav" aria-label="Seções das configurações" className="mb-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
                <div className="flex min-w-max gap-1">
                    {settingsSections.map(section => {
                        const Icon = section.icon;
                        const active = activeSettingsSection === section.key;
                        return (
                            <button key={section.key} type="button" onClick={() => setActiveSettingsSection(section.key)} className={`flex min-w-[132px] items-center gap-2 rounded-xl px-3 py-3 text-left transition sm:min-w-[150px] ${active ? 'bg-slate-950 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}>
                                <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-cyan-200' : 'text-brand-600'}`} />
                                <span><span className="block text-xs font-black">{section.label}</span><span className={`mt-0.5 block text-[10px] font-bold ${active ? 'text-slate-300' : 'text-slate-400'}`}>{section.description}</span></span>
                            </button>
                        );
                    })}
                </div>
            </nav>

            <div className="space-y-6">

                {activeSettingsSection === 'SECURITY' && user.role !== UserRole.SAAS_ADMIN && (
                    <section data-tour="settings-security" className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 p-5 text-white shadow-sm sm:p-6">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-3">
                                <div className="rounded-xl bg-emerald-400/15 p-2.5 text-emerald-300 ring-1 ring-emerald-300/20"><ShieldCheck className="h-5 w-5" /></div>
                                <div>
                                    <h2 className="text-base font-black sm:text-lg">Como funciona o acesso seguro</h2>
                                    <p className="mt-1 max-w-xl text-xs leading-5 text-slate-300">Revise os perfis da equipe, a senha de autorização da gerência e o checklist para trabalhar sem compartilhar o acesso do dono.</p>
                                </div>
                            </div>
                            <Button type="button" onClick={handleOpenAccessTutorial} variant="secondary" className="shrink-0 border-white/10 bg-white text-slate-950 hover:bg-slate-100">
                                <Info className="h-4 w-4" /> Abrir tutorial
                            </Button>
                        </div>
                    </section>
                )}
                
                {activeSettingsSection === 'OPERATION' && user.role === UserRole.OWNER && (
                    <section className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-5 pb-3 border-b border-gray-100">
                            <div className="bg-teal-100 p-2 rounded-lg text-teal-600"><Store className="w-5 h-5" /></div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">Modo de Operação</h2>
                                <p className="text-xs text-gray-500">Escolha a interface principal do PDV e personalize os recursos que combinam com sua operação.</p>
                            </div>
                        </div>

                        <div data-tour="settings-pos-modes" className="grid md:grid-cols-3 gap-3 mb-5">
                            {[
                                { mode: 'STANDARD' as POSMode, title: 'Bar e restaurante', description: 'Mesas, balcão, comandas e garçom.', icon: UtensilsCrossed },
                                { mode: 'DISTRIBUTOR' as POSMode, title: 'Distribuidora', description: 'Venda direta, atacado, caixas e retirada.', icon: Package },
                                { mode: 'MARKET' as POSMode, title: 'Mercado', description: 'Frente de caixa, busca rápida e balança.', icon: Store },
                            ].map(({ mode, title, description, icon: Icon }) => {
                                const active = (currentCompany?.posMode || 'STANDARD') === mode;
                                return (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => handlePosModeChange(mode)}
                                        className={`p-4 rounded-2xl border-2 text-left transition-all ${active ? 'border-brand-500 bg-brand-50 shadow-lg shadow-brand-100' : 'border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50'}`}
                                    >
                                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-3 ${active ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <p className="font-black text-slate-900">{title}</p>
                                        <p className="text-[11px] text-slate-500 mt-1">{description}</p>
                                        {active && <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-brand-700">Interface ativa</p>}
                                    </button>
                                );
                            })}
                        </div>

                        <div data-tour="settings-presets" className="grid sm:grid-cols-3 gap-2 mb-5">
                            <button type="button" onClick={() => handleOperationPreset('bar')} className="p-4 rounded-2xl border border-slate-200 bg-slate-50 hover:border-brand-300 hover:bg-brand-50 text-left transition-colors">
                                <p className="font-black text-slate-900">Perfil Bar</p>
                                <p className="text-[11px] text-slate-500 mt-1">Balcão, estoque e caixa rápido.</p>
                            </button>
                            <button type="button" onClick={() => handleOperationPreset('restaurant')} className="p-4 rounded-2xl border border-slate-200 bg-slate-50 hover:border-brand-300 hover:bg-brand-50 text-left transition-colors">
                                <p className="font-black text-slate-900">Perfil Restaurante</p>
                                <p className="text-[11px] text-slate-500 mt-1">Garçom, cozinha e divisão de conta.</p>
                            </button>
                            <button type="button" onClick={() => handleOperationPreset('snack')} className="p-4 rounded-2xl border border-slate-200 bg-slate-50 hover:border-brand-300 hover:bg-brand-50 text-left transition-colors">
                                <p className="font-black text-slate-900">Perfil Lanchonete</p>
                                <p className="text-[11px] text-slate-500 mt-1">Balcão com preparo na cozinha.</p>
                            </button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-3">
                            {[
                                { id: 'use-counter', label: 'Venda Balcão', description: 'Atalho para atendimento rápido sem mesa.', icon: Store, checked: currentCompany?.useCounterMode ?? false, settings: (checked: boolean) => ({ useCounterMode: checked }) },
                                { id: 'use-waiter', label: 'App Garçom', description: 'Garçom lança pedidos pelo celular.', icon: Users, checked: currentCompany?.useWaiterApp ?? false, settings: (checked: boolean) => ({ useWaiterApp: checked }) },
                                { id: 'use-kitchen', label: 'Tela da Cozinha', description: 'Pedidos com status: novo, preparando, pronto.', icon: ChefHat, checked: currentCompany?.useKitchenDisplay ?? false, settings: (checked: boolean) => ({ useKitchenDisplay: checked }) },
                                { id: 'use-split', label: 'Dividir Conta', description: 'Preparado para dividir por itens/pessoas.', icon: SplitSquareHorizontal, checked: currentCompany?.useSplitBill ?? false, settings: (checked: boolean) => ({ useSplitBill: checked }) },
                                { id: 'use-service', label: 'Taxa de Serviço', description: 'Usa 10% em contas de salão/restaurante. Começa ativa; desligue se não quiser cobrar.', icon: Receipt, checked: currentCompany?.useServiceFee ?? true, settings: (checked: boolean) => ({ useServiceFee: checked }) },
                                { id: 'use-stock', label: 'Gestão de Estoque', description: 'Controle de quantidade e estoque baixo.', icon: Package, checked: currentCompany?.useStockManagement ?? false, settings: (checked: boolean) => ({ useStockManagement: checked }) },
                                { id: 'use-shift', label: 'Fechamento de Caixa', description: 'Abertura, sangria, suprimento e fechamento.', icon: Clock, checked: currentCompany?.useShiftManagement ?? false, settings: (checked: boolean) => ({ useShiftManagement: checked }) },
                                { id: 'use-scale', label: 'Balança / Produto por Kg', description: 'Para buffet, sorveteria, açougue ou por quilo.', icon: Scale, checked: currentCompany?.useScale ?? false, settings: (checked: boolean) => ({ useScale: checked }) },
                                { id: 'use-delivery', label: 'Delivery Online', description: 'Ativa o link público de pedidos e o painel de entregas.', icon: Bike, checked: currentCompany?.useDelivery ?? false, settings: (checked: boolean) => ({ useDelivery: checked }) },
                                { id: 'use-whatsapp', feature: 'useWhatsappAutomation', label: 'Automação WhatsApp', description: 'Envia avisos automáticos quando a API oficial estiver configurada.', icon: MessageCircle, checked: currentCompany?.useWhatsappAutomation ?? false, settings: (checked: boolean) => ({ useWhatsappAutomation: checked }) },
                                { id: 'use-invoice-photo', feature: 'useInvoicePhotoOcr', label: 'Foto da Nota com IA', description: 'Lê a nota por imagem ou PDF e prepara a entrada de estoque.', icon: Camera, checked: currentCompany?.useInvoicePhotoOcr ?? false, settings: (checked: boolean) => ({ useInvoicePhotoOcr: checked }) },
                                { id: 'use-sub-login', alwaysAvailable: true, label: 'Sublogin por funcionário', description: 'Ao abrir o sistema, escolhe dono ou funcionário. Desligado mantém o acesso direto.', icon: ShieldCheck, checked: currentCompany?.useSubLogin ?? false, settings: (checked: boolean) => ({ useSubLogin: checked }) },
                            ].map(({ id, feature, alwaysAvailable, label, description, icon: Icon, checked, settings }) => {
                              const waiterLocked = id === 'use-waiter' && isTradePosMode;
                              const featureKey = feature || Object.keys(settings(true))[0];
                              const planLocked = Boolean(!alwaysAvailable && currentPlanConfig && featureKey && !isFeatureAllowed(featureKey));
                              const locked = waiterLocked || planLocked;
                              const lockText = waiterLocked
                                ? 'Desativado neste modo de PDV'
                                : `Disponível em outro plano. Plano atual: ${planName}.`;
                              return (
                                <div key={id} data-tour={`settings-feature-${id}`} className={`relative flex min-h-[88px] items-center justify-between gap-4 p-4 rounded-2xl border ${locked ? 'bg-slate-100 border-slate-200 opacity-75' : 'bg-gray-50 border-gray-200'}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="bg-white border border-gray-200 rounded-xl p-2 text-brand-600">{planLocked ? <Lock className="w-4 h-4" /> : <Icon className="w-4 h-4" />}</div>
                                        <div className="min-w-0">
                                            <label htmlFor={id} className="text-sm font-black text-gray-800">{label}</label>
                                            <p className="text-[11px] text-gray-500 mt-0.5">{description}</p>
                                        </div>
                                    </div>
                                    {locked && (
                                      <div className="group absolute right-14 top-3">
                                        <button
                                          type="button"
                                          aria-label={lockText}
                                          className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm ring-1 ring-slate-200 hover:text-brand-700"
                                        >
                                          <Info className="h-3.5 w-3.5" />
                                        </button>
                                        <div className="pointer-events-none absolute right-0 top-8 z-20 hidden w-56 rounded-2xl bg-slate-950 px-3 py-2 text-[11px] font-bold leading-snug text-white shadow-xl group-hover:block">
                                          {lockText}
                                        </div>
                                      </div>
                                    )}
                                    <label className={`relative inline-flex items-center ${locked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <input type="checkbox" id={id} checked={locked ? false : checked} disabled={locked} onChange={(event) => handleFeatureToggle(settings(event.target.checked))} className="sr-only peer" />
                                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-brand-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                                    </label>
                                </div>
                              );
                            })}
                        </div>

                        {currentCompany?.useDelivery && (
                          <form onSubmit={handleDeliveryRulesSave} className="mt-5 rounded-2xl border border-brand-100 bg-brand-50/40 p-5 space-y-4">
                            <div className="flex items-start justify-between gap-4"><div><h3 className="font-black text-slate-900">Regras do delivery</h3><p className="mt-1 text-xs text-slate-600">O servidor aplica estas regras mesmo se alguém alterar o navegador.</p></div><label className="flex items-center gap-2 text-xs font-black text-slate-800"><input type="checkbox" checked={deliverySettingsDraft.isOpen} onChange={event => setDeliverySettingsDraft(current => ({ ...current, isOpen: event.target.checked }))} /> Loja aberta</label></div>
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                              <label className={DELIVERY_LABEL_CLASS}>Abre<input type="time" value={deliverySettingsDraft.openingTime || ''} onChange={event => setDeliverySettingsDraft(current => ({ ...current, openingTime: event.target.value }))} className={DELIVERY_FIELD_CLASS} /></label>
                              <label className={DELIVERY_LABEL_CLASS}>Fecha<input type="time" value={deliverySettingsDraft.closingTime || ''} onChange={event => setDeliverySettingsDraft(current => ({ ...current, closingTime: event.target.value }))} className={DELIVERY_FIELD_CLASS} /></label>
                              <label className={DELIVERY_LABEL_CLASS}>Pedido mínimo<input type="number" min="0" step="0.01" value={deliverySettingsDraft.minimumOrder} onChange={event => setDeliverySettingsDraft(current => ({ ...current, minimumOrder: Number(event.target.value || 0) }))} className={DELIVERY_FIELD_CLASS} /></label>
                              <label className={DELIVERY_LABEL_CLASS}>Prazo (min.)<input type="number" min="1" value={deliverySettingsDraft.estimatedMinutes} onChange={event => setDeliverySettingsDraft(current => ({ ...current, estimatedMinutes: Number(event.target.value || 45) }))} className={DELIVERY_FIELD_CLASS} /></label>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-end"><label className={DELIVERY_LABEL_CLASS}>Taxa padrão<input type="number" min="0" step="0.01" value={deliverySettingsDraft.defaultDeliveryFee} onChange={event => setDeliverySettingsDraft(current => ({ ...current, defaultDeliveryFee: Number(event.target.value || 0) }))} className={DELIVERY_FIELD_CLASS} /></label><label className="flex items-center gap-2 pb-3 text-xs font-black text-slate-800"><input type="checkbox" checked={deliverySettingsDraft.allowPickup} onChange={event => setDeliverySettingsDraft(current => ({ ...current, allowPickup: event.target.checked }))} /> Permitir retirada no local</label></div>
                            <div><p className={DELIVERY_LABEL_CLASS}>Bairros atendidos e taxa</p><div className="space-y-2">{deliverySettingsDraft.zones.map((zone, index) => <div key={`${zone.name}-${index}`} className="grid grid-cols-[1fr_120px_auto] gap-2"><input value={zone.name} onChange={event => setDeliverySettingsDraft(current => ({ ...current, zones: current.zones.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item) }))} placeholder="Bairro" className={DELIVERY_FIELD_CLASS} /><input type="number" min="0" step="0.01" value={zone.fee} onChange={event => setDeliverySettingsDraft(current => ({ ...current, zones: current.zones.map((item, itemIndex) => itemIndex === index ? { ...item, fee: Number(event.target.value || 0) } : item) }))} className={DELIVERY_FIELD_CLASS} /><button type="button" onClick={() => setDeliverySettingsDraft(current => ({ ...current, zones: current.zones.filter((_, itemIndex) => itemIndex !== index) }))} className="px-3 text-xs font-black text-red-600">Remover</button></div>)}</div><button type="button" onClick={() => setDeliverySettingsDraft(current => ({ ...current, zones: [...current.zones, { name: '', fee: current.defaultDeliveryFee }] }))} className="mt-3 text-xs font-black text-brand-700">+ Adicionar bairro</button><p className="mt-2 text-[11px] text-slate-500">Se cadastrar bairros, somente os listados poderão pedir delivery. Sem bairros, vale a taxa padrão para todos.</p></div>
                            <Button type="submit" className="h-11 rounded-xl"><Save className="w-4 h-4" /> Salvar regras do delivery</Button>
                          </form>
                        )}

                        <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-100 p-4 flex gap-3 text-sm text-amber-900">
                            <UtensilsCrossed className="w-5 h-5 flex-shrink-0 text-amber-600" />
                            <p><strong>Balança:</strong> só precisa ativar se vender por peso. Para bar comum ou restaurante à la carte, deixa desligado para não complicar.</p>
                        </div>
                    </section>
                )}

                {activeSettingsSection === 'ACTIVITY' && user.role === UserRole.OWNER && currentCompany && (
                    <section data-tour="settings-cleanup" className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5 pb-4 border-b border-gray-100">
                            <div className="flex items-start gap-3">
                                <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600"><DatabaseZap className="w-5 h-5" /></div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-800">Economia do Banco</h2>
                                    <p className="text-xs text-gray-500">Limpeza segura para evitar dados velhos crescendo sem necessidade.</p>
                                </div>
                            </div>
                            <Button type="button" onClick={handleDatabaseCleanup} disabled={isCleaningDatabase} variant="secondary" className="h-11">
                                <DatabaseZap className="w-4 h-4" /> {isCleaningDatabase ? 'Limpando...' : 'Limpar Agora'}
                            </Button>
                        </div>

                        <div className="grid sm:grid-cols-3 gap-3">
                            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Logs</p>
                                <p className="text-sm font-bold text-slate-800 mt-1">Mantém 7 dias</p>
                                <p className="text-[11px] text-slate-500 mt-1">Ações antigas de gerente/garçom saem automaticamente.</p>
                            </div>
                            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Delivery</p>
                                <p className="text-sm font-bold text-slate-800 mt-1">Mantém 90 dias</p>
                                <p className="text-[11px] text-slate-500 mt-1">Só remove entregues/cancelados antigos.</p>
                            </div>
                            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Mesas</p>
                                <p className="text-sm font-bold text-slate-800 mt-1">Limpa vazias travadas</p>
                                <p className="text-[11px] text-slate-500 mt-1">Nunca apaga mesa com itens ou pagamentos.</p>
                            </div>
                        </div>

                        {cleanupResult?.success && (
                            <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-900">
                                <p className="font-black mb-1">Resultado da última limpeza</p>
                                <p>Logs removidos: <strong>{cleanupResult.auditLogsDeleted || 0}</strong> · Delivery removido: <strong>{cleanupResult.deliveryOrdersDeleted || 0}</strong> · Mesas vazias limpas: <strong>{cleanupResult.emptyTablesDeleted || 0}</strong></p>
                            </div>
                        )}

                        <p className="mt-4 text-[11px] text-slate-400">
                            Histórico financeiro de vendas e fechamentos de caixa não é apagado por segurança fiscal e relatórios.
                        </p>
                    </section>
                )}

                {activeSettingsSection === 'OPERATION' && user.role === UserRole.OWNER && !isTradePosMode && (
                  <section data-tour="settings-waiter" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                      <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-100">
                          <div className="bg-cyan-100 p-2 rounded-lg text-cyan-600"><Users className="w-5 h-5" /></div>
                          <div>
                              <h2 className="text-lg font-bold text-gray-800">Acesso de Garçom</h2>
                              <p className="text-xs text-gray-500">Defina um código para acesso rápido de garçons ao app de comandas.</p>
                          </div>
                      </div>
                      <form onSubmit={handleWaiterCodeUpdate} className="space-y-3">
                          <label className="text-xs font-semibold text-gray-600">Código da Empresa (4-6 dígitos)</label>
                          <input 
                              type="number" 
                              value={waiterAccessCode} 
                              onChange={(e) => setWaiterAccessCode(e.target.value)} 
                              className="w-full p-3 border border-gray-300 rounded-lg" 
                              maxLength={6}
                              placeholder="Ex: 1234"
                          />
                          {temporaryWaiterAccess && (
                            <div className="rounded-xl border border-cyan-100 bg-cyan-50 p-3 text-xs text-cyan-900">
                              <p className="font-black flex items-center gap-2"><QrCode className="w-4 h-4" /> Acesso temporário ativo por {temporaryWaiterAccess.expiresInMinutes} minutos</p>
                              <p className="mt-1 break-all">Use este token no campo de código do garçom ou gere um QR Code com ele.</p>
                              <code className="mt-2 block rounded-lg bg-white p-2 break-all text-[10px]">{temporaryWaiterAccess.token}</code>
                            </div>
                          )}
                          <div className="pt-2 flex flex-col sm:flex-row justify-end gap-2">
                            <Button type="button" variant="secondary" onClick={handleTemporaryWaiterAccess}><QrCode className="w-4 h-4" /> Gerar acesso 15 min</Button>
                            <Button type="submit"><Save className="w-4 h-4" /> Salvar Código</Button>
                          </div>
                      </form>
                  </section>
                )}

                {activeSettingsSection === 'ACTIVITY' && user.role !== UserRole.SAAS_ADMIN && (
                    <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-4">
                            <div className="flex items-center gap-3">
                                <div className="rounded-lg bg-orange-100 p-2 text-orange-600"><Clock className="h-5 w-5" /></div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-800">Atividade do estabelecimento</h2>
                                    <p className="text-xs text-gray-500">Ações da empresa nos últimos 7 dias.</p>
                                </div>
                            </div>
                        </div>
                        <div data-tour="settings-calendar" className="mt-5 rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-600">Calendário de atividades</p>
                                    <h3 className="mt-1 flex items-center gap-2 text-xl font-black capitalize text-slate-950"><CalendarDays className="h-5 w-5 text-brand-600" />{auditCalendarMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</h3>
                                </div>
                                <div className="flex gap-2">
                                    <button type="button" aria-label="Mês anterior" onClick={() => changeAuditCalendarMonth(-1)} className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200"><ChevronLeft className="h-5 w-5" /></button>
                                    <button type="button" aria-label="Próximo mês" onClick={() => changeAuditCalendarMonth(1)} className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200"><ChevronRight className="h-5 w-5" /></button>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-7 gap-2 text-center">
                                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => <span key={day} className="text-[9px] font-black uppercase tracking-widest text-slate-400">{day}</span>)}
                            </div>
                            <div className="mt-2 grid grid-cols-7 gap-2">
                                {auditCalendarDays.map(day => {
                                    const isSelected = auditDate ? auditDate === day.key : day.isToday;
                                    return (
                                        <button key={day.key} type="button" onClick={() => { setAuditDate(day.key); setAuditCalendarMonth(new Date(day.date.getFullYear(), day.date.getMonth(), 1)); }} className={`flex min-h-[62px] flex-col items-start justify-between overflow-hidden rounded-2xl border p-2 text-left transition-all sm:min-h-[70px] ${isSelected ? 'border-slate-500 bg-white shadow-md ring-1 ring-slate-300' : 'border-slate-100 bg-slate-50/70 hover:border-brand-200 hover:bg-white'} ${day.isCurrentMonth ? 'opacity-100' : 'opacity-35'}`}>
                                            <span className={`text-sm font-black ${isSelected ? 'text-brand-700' : 'text-slate-700'}`}>{day.date.getDate()}</span>
                                            {day.logCount > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-black text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{day.logCount}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-400">
                                <span>{auditDate ? `Filtrando ${new Date(`${auditDate}T12:00:00`).toLocaleDateString('pt-BR')}` : 'Últimos 7 dias · selecione um dia'}</span>
                                {auditDate && <button type="button" onClick={() => setAuditDate('')} className="font-bold text-brand-700 transition hover:text-brand-900">Mostrar últimos 7 dias</button>}
                            </div>
                        </div>
                        <div data-tour="settings-audit-list" className="space-y-2">
                            {visibleAuditLogs.length === 0 ? (
                                <p className="rounded-lg bg-gray-50 py-4 text-center text-sm text-gray-400">{auditDate ? 'Nenhuma ação nesta data.' : 'Nenhuma ação nos últimos 7 dias.'}</p>
                            ) : (
                                visibleAuditLogs.map(log => (
                                    <div key={log.id} className="flex justify-between items-start text-sm p-3 bg-gray-50 rounded-lg border border-gray-100">
                                        <div>
                                            <span className="font-semibold text-gray-700 block">{log.action}</span>
                                            <span className="text-xs text-gray-500">Por: <span className="font-bold text-brand-600">{log.managerName}</span></span>
                                        </div>
                                        <span className="ml-2 whitespace-nowrap text-right font-mono text-xs text-gray-400">{new Date(log.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </section>
                )}

                {activeSettingsSection === 'TEAM' && user.role === UserRole.OWNER && staffCompanyId && (
                    <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6 pb-4 border-b border-gray-100">
                            <div className="flex items-start gap-3">
                                <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><Users className="w-5 h-5" /></div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-800">Equipe e permissões</h2>
                                    <p className="text-xs text-gray-500">Somente o dono cadastra funcionários. Cada pessoa entra pelo login inicial com seu próprio e-mail e senha.</p>
                                </div>
                            </div>
                            <span className="w-fit rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-700">Cadastro interno</span>
                        </div>

                        <div data-tour="settings-staff-list" className="mb-6 space-y-2">
                            {staffMembers.length === 0 ? (
                                <p className="rounded-lg bg-gray-50 py-4 text-center text-sm text-gray-400">Nenhum funcionário cadastrado ainda.</p>
                            ) : staffMembers.map(staff => (
                                <div key={staff.id} className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="font-bold text-sm text-gray-800">{staff.name}</p>
                                            <span className="rounded-full bg-white px-2 py-1 text-[10px] font-black uppercase tracking-wider text-brand-700 ring-1 ring-gray-200">{staffProfileOptions.find(option => option.key === staff.accessProfile)?.label || 'Funcionário'}</span>
                                        </div>
                                        <p className="mt-1 truncate text-xs text-gray-500">{staff.email}</p>
                                        <p className="mt-1 text-[11px] text-gray-400">{(staff.accessPermissions || []).map(permission => staffPermissionOptions.find(option => option.key === permission)?.label).filter(Boolean).join(' · ') || 'Permissões padrão'}</p>
                                    </div>
                                    <div className="flex shrink-0 gap-2">
                                        <Button type="button" size="sm" variant="secondary" onClick={() => handleEditStaff(staff)}>Editar</Button>
                                        <button type="button" onClick={() => handleDeleteStaff(staff)} className="rounded-lg p-2 text-red-400 transition hover:bg-red-50 hover:text-red-600" aria-label={`Excluir acesso de ${staff.name}`}><Trash2 className="h-4 w-4" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <form data-tour="settings-staff-form" onSubmit={handleSaveStaff} className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4 sm:p-5">
                            <div className="mb-4 flex items-center justify-between gap-3">
                                <div>
                                    <h3 className="text-sm font-black text-gray-800">{editingStaffId ? 'Editar acesso' : 'Novo funcionário'}</h3>
                                    <p className="mt-1 text-[11px] text-gray-500">{editingStaffId ? 'Deixe a senha vazia para manter a atual.' : 'A senha será usada no login inicial do funcionário.'}</p>
                                </div>
                                {editingStaffId && <button type="button" onClick={resetStaffForm} className="text-xs font-bold text-gray-500 hover:text-brand-700">Cancelar edição</button>}
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                                <input type="text" placeholder="Nome completo" value={staffForm.name} onChange={event => setStaffForm(current => ({ ...current, name: event.target.value }))} className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-brand-500" required />
                                <input type="email" placeholder="E-mail individual" value={staffForm.email} onChange={event => setStaffForm(current => ({ ...current, email: event.target.value }))} className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-brand-500" required />
                                <input type="password" placeholder={editingStaffId ? 'Nova senha (opcional)' : 'Senha com 8+ caracteres'} value={staffForm.password} onChange={event => setStaffForm(current => ({ ...current, password: event.target.value }))} className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none focus:border-brand-500" required={!editingStaffId} />
                                <select data-tour="settings-staff-profile" value={staffForm.accessProfile} onChange={event => handleStaffProfileChange(event.target.value as StaffAccessProfile)} className="w-full rounded-xl border border-gray-200 bg-white p-3 text-sm font-bold text-gray-700 outline-none focus:border-brand-500">
                                    {staffProfileOptions.map(option => <option key={option.key} value={option.key}>{option.label} — {option.description}</option>)}
                                </select>
                            </div>

                            <div data-tour="settings-staff-permissions" className="mt-4">
                                <div className="mb-2 flex items-center justify-between gap-3">
                                    <p className="text-xs font-black uppercase tracking-wider text-gray-600">Permissões deste acesso</p>
                                    <p className="text-[11px] font-bold text-gray-400">{staffForm.accessPermissions.length} selecionada(s)</p>
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {staffPermissionOptions.map(permission => {
                                        const checked = staffForm.accessPermissions.includes(permission.key);
                                        return (
                                            <label key={permission.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${checked ? 'border-brand-200 bg-white shadow-sm' : 'border-gray-200 bg-white/60 hover:border-brand-200'}`}>
                                                <input type="checkbox" checked={checked} onChange={() => toggleStaffPermission(permission.key)} className="mt-1 h-4 w-4 accent-brand-600" />
                                                <span><span className="block text-sm font-bold text-gray-800">{permission.label}</span><span className="mt-0.5 block text-[11px] text-gray-500">{permission.description}</span></span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div data-tour="settings-staff-save" className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                                {editingStaffId && <Button type="button" variant="secondary" onClick={resetStaffForm}>Cancelar</Button>}
                                <Button type="submit"><Save className="h-4 w-4" /> {editingStaffId ? 'Salvar alterações' : 'Criar acesso'}</Button>
                            </div>
                        </form>

                        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
                            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                            <p><strong>Importante:</strong> funcionário não cria conta por conta própria. Se o acesso for removido, a sessão dele perde a autorização no próximo login.</p>
                        </div>
                    </section>
                )}

                {activeSettingsSection === 'TEAM' && user.role === UserRole.OWNER && (
                    <section data-tour="settings-manager" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-6 pb-2 border-b border-gray-100">
                            <div className="bg-purple-100 p-2 rounded-lg text-purple-600"><ShieldCheck className="w-5 h-5" /></div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">Autorizações da gerência</h2>
                                <p className="text-xs text-gray-500">Senha extra para confirmar ações críticas. Não cria uma conta de funcionário. Máximo de 2.</p>
                            </div>
                        </div>

                        <div className="mb-6 space-y-2">
                            {managers.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-4 bg-gray-50 rounded-lg">Nenhum gerente cadastrado.</p>
                            ) : (
                                managers.map(mgr => (
                                    <div key={mgr.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-sm border border-purple-200">{mgr.name.substring(0,1)}</div>
                                            <p className="font-bold text-sm text-gray-800">{mgr.name}</p>
                                        </div>
                                        <button onClick={() => handleRemoveManager(mgr.id)} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                ))
                            )}
                        </div>

                        <form onSubmit={handleAddManager} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h3 className="text-sm font-bold text-gray-700 mb-3">Nova autorização</h3>
                            {managers.length >= 2 ? (
                                <p className="text-sm text-center text-orange-600 bg-orange-50 p-3 rounded-lg border border-orange-100">Limite de 2 gerentes atingido.</p>
                            ) : (
                                <>
                                    <div className="grid md:grid-cols-3 gap-2 mb-2">
                                        <input type="text" placeholder="Nome (Ex: Gerente Noite)" className="md:col-span-2 w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-brand-500 text-sm" value={newManagerName} onChange={e => setNewManagerName(e.target.value)} required />
                                        <input type="text" placeholder="Senha" className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-brand-500 text-sm" value={newManagerPass} onChange={e => setNewManagerPass(e.target.value)} required />
                                    </div>
                                    <Button type="submit" size="sm" variant="secondary" className="w-full"><Plus className="w-4 h-4" /> Adicionar Acesso</Button>
                                </>
                            )}
                        </form>
                    </section>
                )}

                {activeSettingsSection === 'ACCOUNT' && <section data-tour="settings-profile" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="mb-6 flex items-start gap-3 border-b border-gray-100 pb-4">
                        <div className="bg-brand-100 p-2 rounded-lg text-brand-600"><User className="w-5 h-5" /></div>
                        <div><h2 className="text-lg font-bold text-gray-800">Meu Perfil</h2><p className="mt-1 text-xs text-gray-500">Atualize seu nome e proteja o acesso à sua conta.</p></div>
                    </div>
                    <form onSubmit={handleUserUpdate} className="space-y-4">
                        <div>
                            <label htmlFor="profile-name" className="text-xs font-black uppercase tracking-wider text-gray-500">Nome de exibição</label>
                            <input id="profile-name" type="text" value={userName} onChange={(e) => setUserName(e.target.value)} className="mt-2 w-full rounded-xl border border-gray-300 p-3 text-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-50" required />
                            <p className="mt-1 text-[11px] text-gray-400">É assim que seu nome aparece nos registros do estabelecimento.</p>
                        </div>
                        <div>
                            <label htmlFor="profile-email" className="text-xs font-black uppercase tracking-wider text-gray-500">E-mail de acesso</label>
                            <input id="profile-email" type="email" value={user.email} disabled className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500" />
                            <p className="mt-1 text-[11px] text-gray-400">O e-mail é usado no login e não pode ser alterado nesta tela.</p>
                        </div>

                        {!isChangingPassword ? (
                            <div data-tour="settings-password" className="flex flex-col items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center">
                                <div><p className="text-sm font-black text-slate-800">Senha de acesso</p><p className="mt-1 text-[11px] text-slate-500">Para trocar, você precisará confirmar a senha atual.</p></div>
                                <Button type="button" variant="secondary" onClick={() => setIsChangingPassword(true)}>Alterar senha</Button>
                            </div>
                        ) : (
                            <div data-tour="settings-password" className="rounded-2xl border border-brand-100 bg-brand-50/50 p-4 sm:p-5">
                                <div className="mb-4"><p className="text-sm font-black text-slate-800">Alterar senha</p><p className="mt-1 text-[11px] leading-5 text-slate-500">Digite a senha atual e confirme a nova senha. A alteração só será aceita se a senha anterior estiver correta.</p></div>
                                <div className="space-y-3">
                                    <div><label htmlFor="current-password" className="text-xs font-bold text-slate-600">Senha atual</label><input id="current-password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 bg-white p-3 text-sm outline-none focus:border-brand-500" required /></div>
                                    <div><label htmlFor="new-password" className="text-xs font-bold text-slate-600">Nova senha</label><input id="new-password" type="password" value={userPassword} onChange={(e) => setUserPassword(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 bg-white p-3 text-sm outline-none focus:border-brand-500" minLength={8} required /><p className="mt-1 text-[11px] text-slate-500">Use pelo menos 8 caracteres.</p></div>
                                    <div><label htmlFor="confirm-password" className="text-xs font-bold text-slate-600">Confirmar nova senha</label><input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 bg-white p-3 text-sm outline-none focus:border-brand-500" minLength={8} required /></div>
                                </div>
                                <button type="button" onClick={() => { setIsChangingPassword(false); setCurrentPassword(''); setUserPassword(''); setConfirmPassword(''); }} className="mt-4 text-xs font-bold text-slate-500 transition hover:text-brand-700">Cancelar alteração</button>
                            </div>
                        )}
                        <div className="flex justify-end pt-2"><Button type="submit"><Save className="w-4 h-4" /> {isChangingPassword ? 'Salvar perfil e senha' : 'Salvar perfil'}</Button></div>
                    </form>
                </section>}

                {activeSettingsSection === 'ACCOUNT' && user.role === UserRole.OWNER && currentCompany && (
                    <section data-tour="settings-company" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-6 pb-2 border-b border-gray-100">
                            <div className="bg-blue-100 p-2 rounded-lg text-blue-600"><Building2 className="w-5 h-5" /></div>
                            <div><h2 className="text-lg font-bold text-gray-800">Dados do Estabelecimento</h2><p className="mt-1 text-xs text-gray-500">Essas informações identificam sua empresa no FlowTab.</p></div>
                        </div>
                        <form onSubmit={handleCompanyUpdate} className="space-y-4">
                            <div><label htmlFor="company-name" className="text-xs font-black uppercase tracking-wider text-gray-500">Nome do estabelecimento</label><input id="company-name" type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="mt-2 w-full rounded-xl border border-gray-300 p-3 text-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-50" required /><p className="mt-1 text-[11px] text-gray-400">Nome exibido no sistema e nos comprovantes.</p></div>
                            <div><label htmlFor="company-document" className="text-xs font-black uppercase tracking-wider text-gray-500">CNPJ ou documento</label><input id="company-document" type="text" value={companyDoc} onChange={(e) => setCompanyDoc(e.target.value)} className="mt-2 w-full rounded-xl border border-gray-300 p-3 text-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-50" placeholder="Opcional" /><p className="mt-1 text-[11px] text-gray-400">Use o CNPJ ou outro documento de identificação da empresa.</p></div>
                            <div><label htmlFor="company-phone" className="text-xs font-black uppercase tracking-wider text-gray-500">Telefone do estabelecimento</label><input id="company-phone" type="tel" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} className="mt-2 w-full rounded-xl border border-gray-300 p-3 text-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-50" placeholder="(00) 00000-0000" /><p className="mt-1 text-[11px] text-gray-400">Número para contato e comunicação da empresa.</p></div>
                            <div className="flex justify-end pt-2"><Button type="submit"><Save className="w-4 h-4" /> Atualizar dados</Button></div>
                        </form>
                    </section>
                )}
            </div>
            {tourOpen && <SpotlightTour label="Guia das Configurações" steps={availableTourSteps} stepIndex={tourStep} onStepChange={changeTourStep} onFinish={finishTour} scrollBlock="center" />}
        </div>
      </div>
    </div>
  );
};
